import type { AdvisorActionType, AdvisorFeedback } from './types'

// Single import site for the app: the picker lives with the backend copy banks
// (same seeded-variant algorithm, now with an optional rotation offset), and is
// re-exported here so app modules need only know about this module.
export { pickAdvisorCopy } from './advisor.copy'

/**
 * App-facing advisor copy banks.
 *
 * The per-action reason/difference copy the BACKEND renders lives in
 * 'advisor.copy.ts'. This module holds the copy the APP renders: the coach
 * headline pools (per-action and conversation kinds), the grade/UI labels, and
 * the end-of-hand outcome recap pools (win/loss, correct-fold, fold-win,
 * correction/plan-change).
 *
 * It lives in the convex package — not the app — on purpose: the advisor is one
 * feature with one voice across backend reasons and frontend headlines/recaps,
 * and 'ADVISOR_OUTPUT.md' (generated by '__tests__/advisor_output.gen.test.ts')
 * must be able to enumerate EVERY string the advisor can say from a single
 * test run. The app already runtime-imports from '@ppwr/convex' (e.g.
 * 'shared/constants'), so importing these pools is the same mechanism.
 *
 * Placeholder conventions:
 * - '{recommendedAction}' / '{RecommendedAction}' — the recommended action
 *   phrase, lower-cased / sentence-cased for its grammatical slot.
 * - '{Concept}' — the coaching concept label ("sizing", "price", ...),
 *   sentence-cased.
 * - '{Street}' — the street name, sentence-cased ("Flop changes the plan.").
 * - '{street}' — a plain beginner-friendly street phrase used by the outcome
 *   recaps ("before the shared cards came out").
 * - '{action}' — the action the player took at the hand’s notable spot, as a
 *   noun ("your raise before the flop"); "choice" when there is no notable
 *   spot to name.
 * - '{actionPhrase}' / '{streetDelta}' / '{capStreet}' / '{newPlan}' — fragments
 *   built app-side by the correction/plan-change builders
 *   ('describeActionChange', 'describeStreetDelta', ...); always spliced inside
 *   a sentence, never standalone.
 *
 * Composition contract: every pool is a list of full sentences (or full
 * sentence templates) — no bare fragments.
 */

// ---------------------------------------------------------------------------
// UI labels
// ---------------------------------------------------------------------------

export const ADVISOR_UI_COPY = {
  avatarLabel: 'Evie',
  cardTitle: 'Advisor',
  // The drawer switch is the overall "advisor available" toggle: when on the
  // advisor avatar is shown; tapping the avatar then toggles feedback on/off.
  // The drawer row label. With the three-state segmented control (Beginner /
  // Intermediate / Off) the row IS the advisor setting — "Available" no longer
  // fits. Also reused for the avatar’s on/off accessibility label.
  drawerLabel: 'Advisor',
  // Advice-timing segmented row in the game options drawer (shown only while
  // the advisor toggle is on).
  // Advisor drawer row: a card-color-style segmented control with three
  // states. Internally: Off = `enabled: false`; Beginner = `enabled: true` +
  // `adviceBeforePlay: true` (advice before each play); Intermediate =
  // `enabled: true` + `adviceBeforePlay: false` (feedback after each play).
  // The avatar-tap mute (`feedbackEnabled`) is separate and untouched.
  advisorBeginner: 'Advice & Feedback',
  advisorIntermediate: 'Feedback Only',
  advisorOff: 'Off',
  // Admin tool row under the coaching-level dropdown: resets the welcome
  // bubble’s session budget so the introduction can be re-tested.
  advisorResetWelcome: 'Reset welcome intro',
  gradeLabels: {
    aligned: 'Nice',
    close: 'Close',
    offProfile: 'Adjust'
  } satisfies Record<AdvisorFeedback['grade'], string>
} as const

// ---------------------------------------------------------------------------
// Coach headlines (per-action)
// ---------------------------------------------------------------------------
// The headline already names the action. The supporting line carries one
// reason, not the reason. These pools exist so consecutive aligned/adjusted
// reactions do not read identically; the pick is seeded off the supporting
// line plus a copy offset (see 'useActionAdvisorStore' anti-repeat logic).

export const ADVISOR_HEADLINE_COPY = {
  aligned: [
    "I like this. I’d usually {recommendedAction} here too.",
    "This works well. I’d usually {recommendedAction} here too.",
    "I’m with you here. I’d usually {recommendedAction} as well.",
    "Good choice. I’d {recommendedAction} here too.",
    "That’s a sound play. I’d {recommendedAction} too.",
    'Well played. {RecommendedAction} fits here.',
    "You found it. I’d {recommendedAction} here too.",
    'Right idea. {RecommendedAction} works well.',
    'That’s my line too. {RecommendedAction} fits.',
    'Good decision. {RecommendedAction} is right here.',
    'That’s the play. {RecommendedAction} fits this spot.',
    'That’s just right. {RecommendedAction} is the move.',
    'Same page here. {RecommendedAction} it is.',
    "Solid. I’d {recommendedAction} here too.",
    "That’s the read. I’d usually {recommendedAction} as well.",
    "Good instincts. I’d {recommendedAction} too.",
    'Nice pick. {RecommendedAction} fits.'
  ],
  close: [
    "I like the idea. I’d usually {recommendedAction} here.",
    "You’re close. I’d usually {recommendedAction} here.",
    "The thought is good. I’d usually {recommendedAction} here.",
    "Nearly there. I’d {recommendedAction} here.",
    "Good idea; I’d adjust to {recommendedAction}.",
    'Close. {RecommendedAction} fits a little better.',
    "You’re on the right track. I’d {recommendedAction}.",
    "The plan is close; I’d {recommendedAction} here.",
    "Almost there. I’d {recommendedAction} here.",
    "The idea lands. I’d {recommendedAction} instead.",
    "Close. I’d go with {recommendedAction}.",
    "You’re near it. I’d {recommendedAction} here."
  ],
  offProfile: [
    "I’d usually {recommendedAction} here.",
    'My default here is usually to {recommendedAction}.',
    "I’d lean toward {recommendedAction} in this spot.",
    'This spot works better if you {recommendedAction}.',
    'Try {recommendedAction} here.',
    'The stronger choice is to {recommendedAction}.',
    'I’d change course and {recommendedAction}.',
    'Look to {recommendedAction} in this spot.',
    "I’d {recommendedAction} here instead.",
    'The stronger play is to {recommendedAction}.',
    'A better line is to {recommendedAction}.',
    'My move here would be to {recommendedAction}.'
  ]
} as const

// ---------------------------------------------------------------------------
// Coach headlines (conversation kinds) — reactions that reference an earlier
// decision in the same hand, shown instead of the per-action headline.
// ---------------------------------------------------------------------------

export const ADVISOR_CONVERSATION_HEADLINE_COPY = {
  // Turn-start advice (hint-only): the headline must NOT name the recommended
  // action AND must stay tonally neutral — it cannot read as an endorsement
  // ("This one is worth a look") or a warning, because the supporting line may
  // recommend folding a spot the headline seemed excited about. Pure framing,
  // no take. Larger pool so the anti-repeat rotation does not cycle quickly.
  // The one exception is `advicePremiumHand` below.
  advice: [
    'Here’s my read on this spot.',
    'A thought before you act.',
    'My take on this one.',
    'Let’s talk this spot through.',
    'Before you decide —',
    'Here’s how I see it.',
    'A quick word on this spot.',
    'Here’s my thinking.',
    'My two cents first.',
    'Quick thought before you decide.',
    'Something to weigh here.',
    'Take a moment on this one.',
    'Here’s the setup.',
    'A note before you act.',
    'Let me set the scene.',
    'First, my read.'
  ],
  // Turn-start advice for a premium starting hand (decision.preflopTier ===
  // 'premium': pairs from tens up, ace-king, ace-queen suited, king-queen
  // suited) when the expert recommends putting chips in aggressively (raise,
  // bet, or all-in). Replaces the neutral `advice` pool for that spot only.
  // The neutrality rule does not apply here: an aggressive recommendation can
  // never contradict an enthusiastic headline. Still hint-only, so no action
  // verbs (and no words containing one, such as "better"), and no hand names:
  // the tier spans pairs and unpaired hands, and the supporting line usually
  // names the hand. Avoids "one of the best starting hands", which the
  // supporting line may already say (PREFLOP_TIER_CLAIMS).
  advicePremiumHand: [
    'I like this hand.',
    'Now this is a hand I like.',
    'Oh, I like these cards.',
    'These are the cards you wait for.',
    'Now we are in business.',
    'Great cards. Make them count.',
    'This is a hand to get excited about.',
    'Hands like this are worth the wait.',
    'Now that is a starting hand.',
    'Big hand. I like where this is going.',
    'You picked up a good one.',
    'This is what we have been waiting for.'
  ],
  repeated: [
    'Same {Concept} spot.',
    '{Concept} is coming up again.',
    'Same pattern again.',
    'Another chance to adjust.',
    'Same adjustment here.',
    'This spot needs the same change.',
    'Here’s that pattern again.',
    'One more chance to make the change.'
  ],
  corrected: [
    'That’s the adjustment.',
    'Better decision this time.',
    'Good adjustment.',
    'That fixes it.',
    'Much better this time.',
    'That’s the better line.',
    'You made the change.',
    'That’s right on track.'
  ],
  continued: [
    'Same plan still fits.',
    'The plan still holds.',
    'Same approach here.',
    'Nothing changes yet.',
    'Stay with the same plan.',
    'Keep following the plan.',
    'The same idea applies.',
    'Stay on this course.'
  ],
  reversed: [
    '{Street} changes the plan.',
    'Different spot, different plan.',
    'The plan changes here.',
    'This card changes things.',
    'Time to switch gears.',
    'New information, new plan.',
    'This street calls for a change.',
    'Adjust the plan here.'
  ]
} as const

// ---------------------------------------------------------------------------
// End-of-hand outcome recaps
// ---------------------------------------------------------------------------

export type AdvisorWinFeedbackCategory = 'onPlan' | 'mostlyOnPlan' | 'differentRoute' | 'unreviewed'

export type FoldWinKind =
  | 'soundValue'
  | 'soundBluff'
  | 'valueTooLarge'
  | 'bluffTooLarge'
  | 'aggressionAdjustment'
  | 'sizingAdjustment'
  | 'generic'

export const ADVISOR_OUTCOME_HEADLINES: Record<AdvisorWinFeedbackCategory, readonly string[]> = {
  onPlan: [
    'Nice pot. That was well managed.',
    'Good work. You stayed in control.',
    'Nicely done. That hand had a clear shape.',
    'Strong work. You kept the plan intact.',
    'Good win. Patient and purposeful.',
    'Nice one. You made the key decisions count.',
    'Well played. That line held together.',
    'Good pot. You kept things tidy.',
    'Nicely navigated from start to finish.',
    'That was a composed win.'
  ],
  mostlyOnPlan: [
    'Nice pot. Good adjustments along the way.',
    'Good win. A little creativity paid off.',
    'Nicely done. Mostly steady, with one wrinkle.',
    'Good result. You kept the hand under control.',
    'Nice one. The main idea stayed intact.',
    'Well won. You mixed structure with instinct.',
    'Good pot. Most of the hand was right on track.',
    'Nice work. You chose a thoughtful route.',
    'That came together well.',
    'Good win. You handled the important spots.'
  ],
  differentRoute: [
    'Nice result. You found your own route.',
    'Good win. That was the scenic route.',
    'Nice pot. You trusted a different read.',
    'That worked out. Good result.',
    'Nice one. You changed the script and got there.',
    'Good pot. Your line took an unexpected turn.',
    'You found a way through that one.',
    'Nice result from a different approach.',
    'That line was unconventional, but the pot is yours.',
    'Good win. You made your own plan in that hand.'
  ],
  unreviewed: [
    'Nice hand. Enjoy that pot.',
    'Good win. Keep stacking chips.',
    'Nicely done. That one is yours.',
    'Good work. You brought that hand home.',
    'Nice pot. Well earned.',
    'That is a good one to collect.',
    'Well played. Chips coming your way.',
    'Good result. Add that one to the stack.',
    'Nice win. On to the next hand.',
    'That pot found the right stack.'
  ]
}

export const ADVISOR_OUTCOME_LOSS_HEADLINES: Record<AdvisorWinFeedbackCategory, readonly string[]> = {
  onPlan: [
    'Tough result. Good decisions.',
    'That one did not go your way.',
    'Good decisions, tough result.',
    'A sound line that lost this time.',
    'Tough hand. The process was good.',
    'The result was rough, not the play.'
  ],
  mostlyOnPlan: [
    'Tough hand. Mostly on track.',
    'Close overall, but it lost. Revisit one spot.',
    'Mostly solid, despite the result.',
    'Good process, tough result. One adjustment.',
    'Tough result. The line was close.',
    'Nearly there, but it lost. Revisit one spot.'
  ],
  differentRoute: [
    'Tough result. One spot stands out.',
    'That hand needs a quick review.',
    'One decision changed the hand, and it lost.',
    'Tough one. Revisit the key spot.',
    'Tough result. One clear lesson.',
    'It lost. One adjustment for the next hand.'
  ],
  unreviewed: [
    'Tough hand. On to the next.',
    'That one did not go your way.',
    'No big lesson from that result.',
    'Tough result. Reset and continue.',
    'It lost. Reset and focus on the next hand.',
    'Variance happens. Stay focused.'
  ]
}

export const ADVISOR_OUTCOME_LOSS_SUPPORTING_COPY: Record<AdvisorWinFeedbackCategory, readonly string[]> = {
  onPlan: [
    'The decisions I reviewed were sound.',
    'Your reviewed choices stayed on track.',
    'The line was solid even though it lost.',
    'Your process held up in the key spots.',
    'The result does not change the decisions.',
    'You stayed disciplined throughout the hand.'
  ],
  // `{action}` is the action the player took at the notable spot (the hand’s
  // most important non-aligned reviewed decision) — "your raise before the
  // flop", never an unnamed "choice".
  mostlyOnPlan: [
    'The {action} {street} is worth a quick review.',
    'Most decisions worked; revisit your {action} {street}.',
    'The main adjustment comes from your {action} {street}.',
    'Your line was close; the {action} {street} was the one miss.',
    'Most of the hand held up except your {action} {street}.',
    'Take another look at your {action} {street}.'
  ],
  differentRoute: [
    'The key {action} to revisit came {street}.',
    'A lower-risk choice than your {action} fits better {street}.',
    'Focus the review on your {action} {street}.',
    'The biggest adjustment is your {action} {street}.',
    'A steadier line than your {action} was available {street}.',
    'Revisit the {action} you made {street}.'
  ],
  unreviewed: [
    'No major decision stood out for review.',
    'Your process does not need a major change.',
    'Nothing in that hand needs a major correction.',
    'The hand ended without a key review spot.',
    'No meaningful decision needs another look.',
    'There is nothing urgent to fix from that hand.'
  ]
}

export const ADVISOR_OUTCOME_SUPPORTING_COPY: Record<AdvisorWinFeedbackCategory, readonly string[]> = {
  onPlan: [
    'Your choices stayed close to the plan in every spot I reviewed.',
    'You followed the plan from the first decision through the finish.',
    'The decisions I reviewed all fit the plan well.',
    'You kept each reviewed decision disciplined and on course.',
    'Your line stayed consistent with the plan throughout the hand.',
    'You found the planned action each time a real decision came up.',
    'That was a steady line with no major detours from the plan.',
    'You kept the hand on plan and let the good spots do the work.',
    'Every reviewed choice supported the same clear plan.',
    'You stayed patient and followed the plan all the way through.',
    'The result was good, and the decision process was solid too.',
    'You won the pot while keeping the important choices on track.'
  ],
  mostlyOnPlan: [
    'Most of the hand followed the plan; your {action} {street} improvised a little.',
    'Your main decisions were on track, with your {action} {street} as the small detour.',
    'The overall line fit the plan, even with your {action} {street} off the planned path.',
    'You stayed close to the plan and added your own touch with the {action} {street}.',
    'Most reviewed spots were solid; your {action} {street} was the adventurous one.',
    'The hand was largely on plan, with your {action} {street} as the one adjustment.',
    'You kept the core idea intact despite changing course with your {action} {street}.',
    'Your line was mostly aligned, then your {action} {street} got a little creative.',
    'The key decisions held up; your {action} {street} is the one to revisit.',
    'You followed the main plan and took a slightly different path with your {action} {street}.',
    'Most of that was disciplined; your {action} {street} was closer than perfect.',
    'You were on track overall; your {action} {street} is the one to revisit.'
  ],
  differentRoute: [
    'You took a different line with your {action} {street}, and it worked out this time.',
    'The result was good, though I would still revisit your {action} {street}.',
    'Your {action} {street} went its own way; keep the original plan in mind too.',
    'That pot came your way after your {action} {street} went outside the plan.',
    'You got the result, even though your {action} {street} changed direction.',
    'The win is worth enjoying; your {action} {street} is still a useful review spot.',
    'Your {action} {street} took a different approach, and it worked.',
    'That worked today, while the planned response to your {action} {street} remains the steadier default.',
    'You collected the pot after the extra risk of your {action} {street}.',
    'The outcome was positive, but your {action} {street} is worth another look.',
    'You made your {action} {street} work; keep the safer plan in mind too.',
    'Nice result. The main decision to revisit is still your {action} {street}.'
  ],
  unreviewed: [
    'I did not flag a major decision in that hand, so enjoy the pot.',
    'There was no big decision to correct in that one.',
    'That hand did not produce a major review spot, and the chips are yours.',
    'Nothing needed a strong correction there. Nice result.',
    'A clean result without a major decision to revisit.',
    'No big decision to revisit this time. Take the win.',
    'That one was more about the result than a difficult decision.',
    'You collected the pot without creating a major review point.',
    'No meaningful decision stood out for correction in that hand.',
    'A straightforward win with no major decision to revisit.',
    'That hand stayed simple enough. Nice pot.',
    'There is nothing urgent to revisit from that win.'
  ]
}

export const ADVISOR_CORRECT_FOLD_HEADLINES = [
  'Good fold, despite the board.',
  'The fold was still right.',
  'Right decision, unlucky runout.',
  'Good discipline before the flop.',
  'The later cards do not change it.',
  'Trust that fold.'
] as const

export const ADVISOR_CORRECT_FOLD_COPY = [
  'Those cards would have won, but the fold was right at the time.',
  'The board ran your way later; your original decision was still sound.',
  'You would have won this one, but folding was the disciplined choice.',
  'The result looks tempting, but you made the right decision with the information available.',
  'The runout favored your cards, and the fold was still a good long-term play.',
  'Nice discipline. A lucky runout does not make the earlier fold wrong.'
] as const

// Headlines for fold-wins where the hero MADE the aggressive play that took
// the pot down. The 'generic' fold-win (no hero aggression — e.g. winning the
// big blind after everyone folded) must NOT use these: claiming "the bet got
// through" or "the pressure worked" when the player never bet is false, so
// it gets its own accurate pool below.
export const ADVISOR_FOLD_WIN_HEADLINES = [
  'The bet got through.',
  'Everyone folded.',
  'You won without a showdown.',
  'The pressure worked.',
  'That bet ended the hand.',
  'The pot is yours.'
] as const

// Headlines for a fold-win with NO aggressive play by the hero (the big-blind
// walk, or opponents folding to each other). Every line is true without the
// hero having bet, raised, or called.
export const ADVISOR_FOLD_WIN_GENERIC_HEADLINES = [
  'Everyone folded.',
  'You won without a showdown.',
  'The pot is yours.',
  'No one contested the pot.',
  'The hand folded around to you.',
  'Chips without a contest.'
] as const

export const ADVISOR_FOLD_WIN_COPY: Record<FoldWinKind, readonly string[]> = {
  soundValue: [
    'The bet was sound, even though no weaker hand called.',
    'Good bet with a strong hand; this time everyone folded.',
    'The size made sense, but no one had enough to continue.',
    'You bet with a strong hand and took the pot immediately.',
    'The play was right, even without getting called.',
    'Good bet. The table simply had nothing to continue with.'
  ],
  soundBluff: [
    'Good pressure. You won without showing your cards.',
    'The bluff worked at a sensible level of risk.',
    'You applied pressure and everyone let go.',
    'Good spot to represent strength and take the pot.',
    'The bet did its job without risking too much.',
    'You found the fold you were looking for.'
  ],
  valueTooLarge: [
    'You won, but the size may have pushed weaker hands out.',
    'The bet worked, though a smaller size could get more calls.',
    'You took the pot; a smaller size may earn more calls.',
    'Everyone folded; a smaller bet could keep weaker hands in.',
    'The result was good, but the size made calling difficult.',
    'You won now, though a smaller bet is the better default.'
  ],
  bluffTooLarge: [
    'The bluff worked, but you risked more chips than needed.',
    'Everyone folded, though a smaller bluff could do the same job.',
    'You won the pot, but the bluff size carried extra risk.',
    'The pressure worked; use fewer chips next time.',
    'The result was good, but the bluff was larger than necessary.',
    'You got the folds, though the same idea works with less risk.'
  ],
  aggressionAdjustment: [
    'Nice result. A lower-risk line is the better default.',
    'You got the folds; choose the steadier option next time.',
    'You won the pot. The next step is using less aggression here.',
    'The pressure worked; a measured line is stronger long term.',
    'You took the pot, and checking remains the steadier choice.',
    'Good result. Keep the same confidence with less risk next time.'
  ],
  sizingAdjustment: [
    'The bet worked, but the sizing is still worth adjusting.',
    'Everyone folded; keep the result separate from the bet size.',
    'You won the pot, though the size was not the best fit.',
    'The outcome was good, but revisit how much you bet.',
    'The pressure worked; the sizing can still improve.',
    'You got the fold, but use the recommended size next time.'
  ],
  generic: [
    'Everyone folded, so you won without a showdown.',
    'No one continued, and the pot came your way.',
    'The hand ended when the table folded to you.',
    'You picked up the pot without showing your cards.',
    'The pot came your way without a contest.'
  ]
}

// Correction/plan-change recaps. 'fallback' is used when the app cannot find
// the conversation transition it expected; 'transition' templates are filled
// with fragments built app-side ('{actionPhrase}', '{streetDelta}',
// '{capStreet}', '{newPlan}').

export const ADVISOR_CORRECTION_COPY = {
  fallback: [
    'You corrected course during the hand instead of repeating the earlier decision.',
    'You made the adjustment when the next related spot came up.',
    'You responded well when the same idea came up again.'
  ],
  transition: [
    'You {actionPhrase} {streetDelta}.',
    '{capStreet}, you {actionPhrase}.',
    'You {actionPhrase} {streetDelta}. Nice correction.',
    'You {actionPhrase} {streetDelta}, and that was the right move.',
    'You {actionPhrase} {streetDelta}. That adjustment is what matters.'
  ]
} as const

export const ADVISOR_PLAN_CHANGE_COPY = {
  fallback: [
    'The plan changed as the hand developed, and you adjusted to the new spot.',
    'You stayed flexible when new information changed the best action.',
    'The hand called for a change of direction, and your later decision fit it.'
  ],
  transition: [
    'The plan shifted {streetDelta}, and {newPlan} was the right call.',
    '{capStreet}, the hand called for {newPlan} instead.',
    'You adapted when the plan changed {streetDelta}, and {newPlan} fit the new spot.',
    'A new card {streetDelta} changed what this hand needed, and you made the switch to {newPlan}.',
    '{capStreet}, you changed direction and chose the right action.',
    'The recommendation shifted {streetDelta}, and you followed the new plan with {newPlan}.'
  ]
} as const

export const ADVISOR_LOSS_CORRECTION_COPY = {
  fallback: [
    'The result was tough, but the adjustment was right.',
    'You made a good adjustment later in the hand, even though the result was tough.',
    'The later decision improved on the earlier one, but the hand still did not go your way.'
  ],
  transition: [
    'You {actionPhrase} {streetDelta}, even if the result did not follow.',
    'The result was tough, but you {actionPhrase} {streetDelta}.',
    'You {actionPhrase} {streetDelta}, and the adjustment was correct even though the hand went the other way.',
    '{capStreet}, you {actionPhrase}. That is what counts, even though the hand did not go your way.',
    'The earlier decision needed a correction, and you {actionPhrase} {streetDelta}, though the result was still tough.',
    'You {actionPhrase} {streetDelta}. The process was right, even if the result was not.'
  ]
} as const

export const ADVISOR_LOSS_PLAN_CHANGE_COPY = {
  fallback: [
    'The situation changed, and you adapted well, even though the hand did not go your way.',
    'You changed direction when the hand called for it, but the result was still tough.',
    'You handled the shift in the hand well, though the outcome went the other way.'
  ],
  transition: [
    'The plan shifted {streetDelta}, and {newPlan} was the right call despite the result.',
    '{capStreet}, the hand called for {newPlan}. The adjustment was sound, even though the result was not.',
    'You adapted when the plan changed {streetDelta}, even if the outcome went the other way.',
    'A new card {streetDelta} changed the right move to {newPlan}, and you made the switch, but the hand still did not go your way.',
    'The plan shifted {streetDelta}, and your adjustment showed good awareness despite the tough result.',
    'The recommendation changed {streetDelta}, and you followed the new logic despite a tough result.'
  ]
} as const

// ---------------------------------------------------------------------------
// Welcome introduction (the advisor’s first bubble, at most once per session)
// ---------------------------------------------------------------------------

export const ADVISOR_WELCOME_COPY = {
  headline: "Welcome — I’m " + ADVISOR_UI_COPY.avatarLabel + ", your advisor.",
  supporting: "I’ll share quick notes on your play. Tap me any time to turn me on or off."
} as const

// ---------------------------------------------------------------------------
// Fragment tables the app’s outcome/correction builders splice into the
// template pools above. Pure data on purpose: they live here (not in the
// app) so the generated output doc can enumerate every fragment, while the
// builders' branching logic stays app-side.
// ---------------------------------------------------------------------------

// The {Concept} filler for the conversation headline pools — the coaching
// concept behind the repeated spot, in player-facing words ('price', not
// 'potOdds').
export const ADVISOR_CONCEPT_LABELS = {
  sizing: 'sizing',
  potOdds: 'price',
  valueBetting: 'value',
  handStrength: 'hand strength'
} as const

// Action names as gerunds ('going all in'), used inside action-change
// fragments ("shifted from betting to calling").
export const ADVISOR_ACTION_IN_BRIEF = {
  fold: 'folding',
  check: 'checking',
  call: 'calling',
  bet: 'betting',
  raise: 'raising',
  allin: 'going all-in'
} as const

// Action names as nouns ('your raise before the flop') for the {action}
// filler in the loss-recap pools. Falls back to ADVISOR_NOTABLE_ACTION_FALLBACK
// when the notable action is unrecognized (or there is no notable spot).
export const ADVISOR_ACTION_NOUNS: Record<AdvisorActionType, string> = {
  fold: 'fold',
  check: 'check',
  call: 'call',
  bet: 'bet',
  raise: 'raise',
  allin: 'all-in'
}

export const ADVISOR_NOTABLE_ACTION_FALLBACK = 'choice'

// Short street phrases for the loss-recap {street} filler ('before the flop'),
// or ADVISOR_SHORT_STREET_FALLBACK when the notable street is unknown.
export const ADVISOR_SHORT_STREET = {
  preflop: 'before the flop',
  flop: 'on the flop',
  turn: 'on the turn',
  river: 'on the river'
} as const

export const ADVISOR_SHORT_STREET_FALLBACK = 'in the key spot'

// Longer street phrases for the win-recap {street} filler ('with the first
// three shared cards out'), or ADVISOR_STREET_DESCRIPTION_FALLBACK.
export const ADVISOR_STREET_DESCRIPTIONS = {
  preflop: 'before the shared cards came out',
  flop: 'with the first three shared cards out',
  turn: 'with the fourth shared card out',
  river: 'with all five shared cards out'
} as const

export const ADVISOR_STREET_DESCRIPTION_FALLBACK = 'in one of the key spots'

// How the player’s line changed between two related spots — the {actionPhrase}
// filler (describeActionChange, actionAdvisor.outcome.ts). One-entry pools:
// rotation is a no-op today, but the structure is future-variant-ready, and
// parameterized fragments ride the template renderer ({brief} etc.).
export const ADVISOR_ACTION_CHANGE_COPY = {
  fixedSizing: ['fixed the sizing'],
  kept: ['kept {brief}'],
  mistakeShift: ['shifted from {prior} ({better} would have been better) to {current}'],
  foldToPressure: ['shifted from folding to applying pressure'],
  checkToBet: ['shifted from checking to betting'],
  betToWellTimedFold: ['moved from betting to a well-timed fold'],
  betToCheck: ['scaled back from betting to checking'],
  neutralShift: ['shifted from {prior} to {current}']
} as const

// When the plan changed between two related spots — the {streetDelta} /
// {capStreet} fillers (describeStreetDelta, actionAdvisor.outcome.ts).
export const ADVISOR_STREET_DELTA_COPY = {
  flopCameDown: ['when the flop came down'],
  turnAppeared: ['when the turn card appeared'],
  riverCompleted: ['when the river completed the board'],
  twoStreetSpan: ['from the {priorStreet} to the {currentStreet}'],
  onStreet: ['on the {street}']
} as const
